import React, { useState, useCallback, useMemo } from "react";
import Fade from "react-reveal/Fade";
import "./OrganizationalChart.css";

// Organizational Chart Data - Hierarchical Structure
const orgChartData = {
  id: "board",
  name: "BOARD",
  level: "board",
  children: [
    {
      id: "shariah-advisor",
      name: "Shariah Advisor",
      level: "advisory",
      position: "left",
      children: [
        {
          id: "shariah-compliance",
          name: "Shariah Compliance Officer",
          level: "department",
        },
      ],
    },
    {
      id: "ceo",
      name: "Chief Executive Officer",
      level: "executive",
      position: "center",
      dottedToAudit: true,
      children: [
        {
          id: "cfo",
          name: "Chief Financial Officer & Company Secretary",
          level: "cxo",
          dottedToBoard: true,
          children: [
            { id: "finance", name: "Finance & Accounts", level: "department" },
            { id: "admin", name: "Administration", level: "department" },
            { id: "legal", name: "Legal & Corporate Affairs", level: "department" },
            { id: "it", name: "Information Technology", level: "department" },
            {
              id: "compliance",
              name: "Compliance & Risk Management",
              level: "department",
              children: [
                { id: "compliance-officer", name: "Compliance Officer", level: "subdepartment" },
              ],
            },
          ],
        },
        {
          id: "sales-marketing",
          name: "Head of Sales & Marketing",
          level: "head",
          children: [
            { id: "branch", name: "Branch Network", level: "department" },
            { id: "agents", name: "Agents", level: "department" },
            { id: "brokers", name: "Brokers", level: "department" },
            { id: "aggregators", name: "Aggregators (Branch & Direct)", level: "department" },
            { id: "agency", name: "Agency Department", level: "department" },
          ],
        },
        {
          id: "operations",
          name: "Head of Operations",
          level: "head",
          children: [
            { id: "uw", name: "U/w all classes", level: "department" },
            { id: "claims", name: "Claims all Classes", level: "department" },
            { id: "treaty", name: "Treaty", level: "department" },
          ],
        },
        {
          id: "retail-sales",
          name: "Head of Retail Sales",
          level: "head",
          children: [
            { id: "retail-agent", name: "Retail Sales Agent", level: "department" },
            { id: "customer-service", name: "Customer Service", level: "department" },
            { id: "telesales", name: "Telesales", level: "department" },
            { id: "retail-back", name: "Retail Sales (Back Office)", level: "department" },
          ],
        },
        {
          id: "hr",
          name: "Human Resources",
          level: "head",
        },
        {
          id: "products",
          name: "Products & Development",
          level: "department",
        },
        {
          id: "marketing",
          name: "Marketing",
          level: "department",
        },
        {
          id: "exec-secretariat",
          name: "Executive Secretariat",
          level: "department",
        },
      ],
    },
    {
      id: "audit-committee",
      name: "Board Audit Committee",
      level: "committee",
      position: "right",
      children: [
        {
          id: "internal-audit",
          name: "Internal Audit Department",
          level: "department",
        },
      ],
    },
  ],
};

// Node Component
const OrgNode = React.memo(({ node, isExpanded, onToggle, hasChildren, isMobile, dottedToBoard, dottedToAudit }) => {
  const levelClasses = {
    board: "org-node--board",
    executive: "org-node--executive",
    cxo: "org-node--cxo",
    advisory: "org-node--advisory",
    committee: "org-node--committee",
    head: "org-node--head",
    department: "org-node--department",
    subdepartment: "org-node--subdepartment",
  };

  const levelClass = levelClasses[node.level] || "org-node--department";
  const hasExpandable = hasChildren && isMobile;

  return (
    <div
      className={`org-node ${levelClass} ${dottedToBoard ? "org-node--dotted-to-board" : ""} ${dottedToAudit ? "org-node--dotted-to-audit" : ""}`}
      onClick={hasExpandable ? onToggle : undefined}
      role={hasExpandable ? "button" : undefined}
      tabIndex={hasExpandable ? 0 : undefined}
      onKeyDown={hasExpandable ? (e) => e.key === "Enter" && onToggle() : undefined}
      aria-expanded={hasExpandable ? isExpanded : undefined}
    >
      <div className="org-node__content">
        <span className="org-node__title">{node.name}</span>
        {hasExpandable && (
          <span className={`org-node__toggle ${isExpanded ? "org-node__toggle--expanded" : ""}`}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        )}
      </div>
    </div>
  );
});

OrgNode.displayName = "OrgNode";

// Recursive Tree Component for Desktop
const TreeBranch = React.memo(({ node, depth = 0 }) => {
  const hasChildren = node.children && node.children.length > 0;

  // Build class names
  let branchClasses = `org-tree-branch org-tree-branch--depth-${Math.min(depth, 4)}`;
  if (node.dottedToBoard) branchClasses += " org-tree-branch--dotted-to-board";
  if (node.dottedToAudit) branchClasses += " org-tree-branch--dotted-to-audit";

  return (
    <div className={branchClasses}>
      <div className="org-tree-node-wrapper">
        {depth > 0 && (
          <div className="org-tree-connector">
            <div className="org-tree-connector__vertical" />
          </div>
        )}
        <OrgNode node={node} hasChildren={hasChildren} isMobile={false} dottedToBoard={node.dottedToBoard} dottedToAudit={node.dottedToAudit} />
        {/* Dotted line from CEO to Audit */}
        {node.dottedToAudit && (
          <div className="org-ceo-audit-dotted">
            <div className="org-ceo-audit-dotted__horizontal" />
            <div className="org-ceo-audit-dotted__vertical" />
            <div className="org-ceo-audit-dotted__horizontal2" />
            <div className="org-ceo-audit-dotted__vertical2" />
            <div className="org-ceo-audit-dotted__dot" />
          </div>
        )}
      </div>

      {hasChildren && (
        <div className="org-tree-children">
          {node.children.map((child) => (
            <TreeBranch key={child.id} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
});

TreeBranch.displayName = "TreeBranch";

// Mobile Collapsible Tree Component
const MobileTreeBranch = React.memo(({ node, depth = 0, expandedNodes, toggleNode }) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expandedNodes.has(node.id);

  return (
    <div className={`org-mobile-branch org-mobile-branch--depth-${Math.min(depth, 4)}`}>
      <div className="org-mobile-node-wrapper">
        <OrgNode
          node={node}
          isExpanded={isExpanded}
          onToggle={() => toggleNode(node.id)}
          hasChildren={hasChildren}
          isMobile={true}
        />
      </div>

      {hasChildren && isExpanded && (
        <div className="org-mobile-children">
          {node.children.map((child) => (
            <MobileTreeBranch
              key={child.id}
              node={child}
              depth={depth + 1}
              expandedNodes={expandedNodes}
              toggleNode={toggleNode}
            />
          ))}
        </div>
      )}
    </div>
  );
});

MobileTreeBranch.displayName = "MobileTreeBranch";

// Main Chart Component
function OrganizationalChart() {
  const [expandedNodes, setExpandedNodes] = useState(new Set(["board", "ceo"]));
  const [viewMode, setViewMode] = useState("auto"); // 'auto', 'tree', 'compact'

  const toggleNode = useCallback((nodeId) => {
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (next.has(nodeId)) {
        next.delete(nodeId);
      } else {
        next.add(nodeId);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    const getAllIds = (node) => {
      let ids = [node.id];
      if (node.children) {
        node.children.forEach((child) => {
          ids = ids.concat(getAllIds(child));
        });
      }
      return ids;
    };
    setExpandedNodes(new Set(getAllIds(orgChartData)));
  }, []);

  const collapseAll = useCallback(() => {
    setExpandedNodes(new Set(["board"]));
  }, []);

  // SVG Icons
  const chartIcon = useMemo(
    () => (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <path d="M10 6.5h4" />
        <path d="M10 17.5h4" />
        <path d="M6.5 10v4" />
        <path d="M17.5 10v4" />
      </svg>
    ),
    []
  );

  return (
    <section className="org-chart-section" aria-label="Organizational Chart">
      <div className="org-chart-container">
        <Fade down delay={200}>
          <div className="org-chart-header">
            <div className="org-chart-header__icon">{chartIcon}</div>
            <h2 className="org-chart-header__title oswald">Organizational Chart</h2>
            <p className="org-chart-header__subtitle">
              Leadership structure and reporting lines at Salaam Takaful Limited
            </p>
          </div>
        </Fade>

        {/* Official Organizational Chart Image - displayed prominently */}
        <Fade up delay={250}>
          <div className="org-chart-image-block">
            <div className="org-chart-image-wrapper">
              <img
               src={`${process.env.REACT_APP_URL_ASSETS}Assets/charts/organizationalchart.png`}
                alt="Salaam Takaful Limited - Organizational Chart"
                className="org-chart-image"
              />
            </div>
          </div>
        </Fade>

        {/* <Fade up delay={300}>
          <div className="org-chart-controls">
            <div className="org-chart-controls__view">
              <button
                className={`org-chart-btn ${viewMode === "auto" ? "org-chart-btn--active" : ""}`}
                onClick={() => setViewMode("auto")}
                aria-pressed={viewMode === "auto"}
                aria-label="Auto view mode"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="18" height="18" rx="2" />
                  <path d="M3 9h18" />
                  <path d="M9 21V9" />
                </svg>
                <span>Auto</span>
              </button>
              <button
                className={`org-chart-btn ${viewMode === "tree" ? "org-chart-btn--active" : ""}`}
                onClick={() => setViewMode("tree")}
                aria-pressed={viewMode === "tree"}
                aria-label="Tree view mode"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 3v6" />
                  <path d="M5 9h14" />
                  <path d="M5 9v6" />
                  <path d="M19 9v6" />
                  <path d="M12 9v6" />
                  <rect x="2" y="15" width="6" height="6" rx="1" />
                  <rect x="9" y="15" width="6" height="6" rx="1" />
                  <rect x="16" y="15" width="6" height="6" rx="1" />
                </svg>
                <span>Tree View</span>
              </button>
              <button
                className={`org-chart-btn ${viewMode === "compact" ? "org-chart-btn--active" : ""}`}
                onClick={() => setViewMode("compact")}
                aria-pressed={viewMode === "compact"}
                aria-label="Compact view mode"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="3" y1="6" x2="21" y2="6" />
                  <line x1="3" y1="12" x2="21" y2="12" />
                  <line x1="3" y1="18" x2="21" y2="18" />
                </svg>
                <span>Compact</span>
              </button>
            </div>

            <div className="org-chart-controls__actions">
              <button className="org-chart-btn org-chart-btn--secondary" onClick={expandAll} aria-label="Expand all nodes">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 3h6v6" />
                  <path d="M9 21H3v-6" />
                  <path d="M21 3l-7 7" />
                  <path d="M3 21l7-7" />
                </svg>
                <span>Expand All</span>
              </button>
              <button className="org-chart-btn org-chart-btn--secondary" onClick={collapseAll} aria-label="Collapse all nodes">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 14h6v6" />
                  <path d="M20 10h-6V4" />
                  <path d="M14 10l7-7" />
                  <path d="M3 21l7-7" />
                </svg>
                <span>Collapse</span>
              </button>
            </div>
          </div>
        </Fade> */}

        {/* <Fade up delay={400}>
          <div className={`org-chart-wrapper org-chart-wrapper--${viewMode}`}>
            <div className="org-chart-desktop">
              <div className="org-chart-tree">
                <TreeBranch node={orgChartData} />
              </div>
            </div>

            <div className="org-chart-mobile">
              <MobileTreeBranch node={orgChartData} expandedNodes={expandedNodes} toggleNode={toggleNode} />
            </div>
          </div>
        </Fade> */}

        {/* <Fade up delay={500}>
          <footer className="org-chart-footer">
            <div className="org-chart-legend">
              <div className="org-chart-legend__item">
                <span className="org-chart-legend__color org-chart-legend__color--board" />
                <span>Board</span>
              </div>
              <div className="org-chart-legend__item">
                <span className="org-chart-legend__color org-chart-legend__color--executive" />
                <span>Executive</span>
              </div>
              <div className="org-chart-legend__item">
                <span className="org-chart-legend__color org-chart-legend__color--head" />
                <span>Department Heads</span>
              </div>
              <div className="org-chart-legend__item">
                <span className="org-chart-legend__color org-chart-legend__color--department" />
                <span>Departments</span>
              </div>
              <div className="org-chart-legend__item">
                <span className="org-chart-legend__line org-chart-legend__line--solid" />
                <span>Reporting Line</span>
              </div>
              <div className="org-chart-legend__item">
                <span className="org-chart-legend__line org-chart-legend__line--dotted" />
                <span>Associated Line</span>
              </div>
            </div>
          </footer>
        </Fade> */}
      </div>
    </section>
  );
}

export default OrganizationalChart;
